import { Form, Input } from "antd";
import { AppButton } from "components/button/AppButton";
import avatar from "assets/images/default.webp";
import { validateURL } from "utils/formHelpers/validations";
import {
  QUERY_KEY_FOR_TALENT_PROFILE,
  useGetTalentProfile,
} from "features/settings/hooks/useGetTalentProfile";
import { ChangeEvent, useEffect, useState } from "react";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { IUpdateTProfile, FileWithPreview } from "features/settings/types";
import { useUpdateTalentProfile } from "features/settings/hooks/useUpdateTalentProfile";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import { useSignIn } from "react-auth-kit";
import { resizeImageToSquare, resizeCoverPhoto } from "utils/resizephoto";
import * as pdfjs from "pdfjs-dist";
import ProfileSidebar from "./TalentProfileSidebar";
import { ProfileNavigationButtons } from "./ProfileNavigationButtons";
import { useNavigate } from "react-router-dom";
import { getNextSection } from "../../services/profileNavigationService";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DEFAULT_COVER = "/assets/cover_image.png";

export const TalentURLs = () => {
  const { data: remoteData, isLoading: isGetTPLoading } = useGetTalentProfile();
  const [form] = Form.useForm();
  const { token, userInfo } = useGetUserInfo();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate, isLoading: isUpdateTPLoading } = useUpdateTalentProfile();
  const [file, setFile] = useState<FileWithPreview | null>(null);
  const [resizedBlob, setResizedBlob] = useState<Blob | null>(null);
  const [coverPhotoBlob, setCoverPhotoBlob] = useState<Blob | null>(null);
  const [dualNationality, setDualNationality] = useState<boolean>(false);
  const [active, setActive] = useState("URLs");
  const signIn = useSignIn();

  useEffect(() => {
    setDualNationality(!!remoteData?.data.dual_country);
    form.setFieldsValue({
      linkedin: remoteData?.data.linkedin,
      github: remoteData?.data.github,
      behance: remoteData?.data.behance,
      facebook: remoteData?.data.facebook,
      x: remoteData?.data.x,
      instagram: remoteData?.data.instagram,
      portfolio: remoteData?.data.portfolio,
    });
  }, [form, remoteData]);

  const handleSubmit = (
    data: IUpdateTProfile,
    shouldNavigate: boolean = true,
  ) => {
    const props: IUpdateTProfile = {
      first_name: remoteData?.data?.first_name
        ? remoteData.data.first_name
        : "",
      middle_name: remoteData?.data.middle_name
        ? remoteData.data.middle_name
        : "",
      last_name: remoteData?.data.last_name ? remoteData.data.last_name : "",
      gender: remoteData?.data.gender ? remoteData.data.gender : "",
      executive_summary: remoteData?.data.executive_summary
        ? remoteData.data.executive_summary
        : "",
      photo: resizedBlob ? resizedBlob : null,
      cover_photo: coverPhotoBlob ? coverPhotoBlob : null,
      professional_goal: remoteData?.data.professional_goal
        ? remoteData.data.professional_goal
        : "",
      phone: remoteData?.data.phone ? remoteData.data.phone : "",
      country: remoteData?.data.country ? remoteData.data.country : "",
      dual_country: dualNationality ? data.dual_country : null,
      experience_level: remoteData?.data.experience_level
        ? remoteData.data.experience_level
        : "",
      current_position: remoteData?.data.current_position
        ? remoteData.data.current_position
        : "",
      sector_id: remoteData?.data.sector_id ? remoteData.data.sector_id : "",
      city: remoteData?.data.city ? remoteData.data.city : "",
      language: remoteData?.data?.language ? remoteData.data.language : [],
      prefered_industries: remoteData?.data.prefered_industries
        ? remoteData.data.prefered_industries
        : "",
      prefered_locations: remoteData?.data.prefered_locations
        ? remoteData.data.prefered_locations
        : "",
      linkedin: data.linkedin ? data.linkedin : "",
      github: data.github ? data.github : "",
      behance: data.behance ? data.behance : "",
      facebook: data.facebook ? data.facebook : "",
      x: data.x ? data.x : "",
      open_to_work: remoteData?.data.open_to_work
        ? remoteData.data.open_to_work
        : true,
      relocate: remoteData?.data.relocate ? remoteData.data.relocate : false,
      instagram: data.instagram ? data.instagram : "",
      portfolio: data.portfolio ? data.portfolio : "",
      current_location: remoteData?.data.current_location
        ? remoteData.data.current_location
        : "",
      skills: remoteData?.data?.skills ? remoteData?.data.skills : [],
      token,
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Profile Update Successful",
          duration: 4.5,
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_TALENT_PROFILE],
        });

        if (shouldNavigate) {
          const nextSection = getNextSection(window.location.pathname);
          if (nextSection) {
            navigate(nextSection.link);
          }
        }
      },
    });
  };

  const handleSaveAndNext = () => {
    form
      .validateFields()
      .then((values) => {
        handleSubmit(values, true);
      })
      .catch((errorInfo) => {
        openNotification({
          title: "Validation Error",
          state: "error",
          description: "Please check your URLs and try again.",
          duration: 4.5,
        });
      });
  };

  const handleNext = () => {
    const nextSection = getNextSection(window.location.pathname);
    if (nextSection) {
      navigate(nextSection.link);
    }
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile({
        ...selectedFile,
        preview: URL.createObjectURL(selectedFile),
      } as FileWithPreview);
      try {
        const resizedImageBlob = await resizeImageToSquare(selectedFile, 200);
        setResizedBlob(resizedImageBlob);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  const coverPhotoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile({
        ...selectedFile,
        preview: URL.createObjectURL(selectedFile),
      } as FileWithPreview);
      try {
        const resizedImageBlob = await resizeCoverPhoto(selectedFile);
        setCoverPhotoBlob(resizedImageBlob);
      } catch (error) {
        console.error("Error resizing image:", error);
      }
    }
  };

  return (
    <>
      <Form
        form={form}
        onFinish={(values) => handleSubmit(values, false)}
        disabled={isGetTPLoading}
      >
        <div className="flex justify-between mb-4">
          <div>
            <h4 className="font-semibold text-lg text-primary">
              Personal info
            </h4>
            <h3 className="text-sm">Update your details here.</h3>
          </div>
        </div>
        <hr />
        <div className="grid grid-cols-1 md:grid-cols-5 my-3 md:my-5 gap-3">
          <Form.Item noStyle name="image">
            <Input type="hidden" />
          </Form.Item>

          <div className="relative col-span-5">
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              id="fileInput"
              onChange={coverPhotoChange}
            />
            <i
              className="ri-edit-2-fill absolute top-2 right-2 p-2 bg-white rounded-full cursor-pointer text-primary hover:text-primary"
              onClick={() => document.getElementById("fileInput")?.click()}
            ></i>
            <img
              src={
                coverPhotoBlob
                  ? URL.createObjectURL(coverPhotoBlob)
                  : (remoteData?.data.user.cover_photo || DEFAULT_COVER)
              }
              alt="cover"
              className="w-full h-full object-cover"
            />

            <div className="absolute -bottom-6 left-4">
              <div className="relative w-fit">
                <img
                  src={
                    resizedBlob
                      ? URL.createObjectURL(resizedBlob)
                      : (remoteData?.data.dp ?? avatar)
                  }
                  alt="avatar"
                  className="h-20 w-20 rounded-full border-2 border-white shadow-lg"
                />

                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="cover_photo"
                  onChange={handleFileChange}
                />

                <i
                  className="ri-edit-2-fill absolute -top-1 -right-1 bg-white rounded-full p-1 shadow 
              cursor-pointer text-primary hover:text-primary"
                  onClick={() =>
                    document.getElementById("cover_photo")?.click()
                  }
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <ProfileSidebar active={active} onSelect={setActive} />

          <div className="md:col-span-5 p-4 rounded shadow-lg w-full">
            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Linkedin profile (Optional)</h3>
              <span className="col-span-4 w-full">
                <Form.Item
                  name="linkedin"
                  className="col-span-2"
                  rules={[{ validator: validateURL }]}
                >
                  <Input placeholder="https://linkedin.com" />
                </Form.Item>
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Portfolio (Optional)</h3>
              <span className="col-span-4 w-full">
                <Form.Item
                  name="portfolio"
                  className="col-span-2"
                  rules={[{ validator: validateURL }]}
                >
                  <Input placeholder="https://yourportfolio.com" />
                </Form.Item>
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Github profile (Optional)</h3>
              <span className="col-span-4 w-full">
                <Form.Item
                  name="github"
                  className="col-span-2"
                  rules={[{ validator: validateURL }]}
                >
                  <Input placeholder="https://github.com" />
                </Form.Item>
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Behance profile (Optional)</h3>
              <span className="col-span-4 w-full">
                <Form.Item
                  name="behance"
                  className="col-span-2"
                  rules={[{ validator: validateURL }]}
                >
                  <Input placeholder="https://behance.com" />
                </Form.Item>
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Facebook profile (Optional)</h3>
              <span className="col-span-4 w-full">
                <Form.Item
                  name="facebook"
                  className="col-span-2"
                  rules={[{ validator: validateURL }]}
                >
                  <Input placeholder="https://facebook.com" />
                </Form.Item>
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>X profile (Optional)</h3>
              <span className="col-span-4 w-full">
                <Form.Item
                  name="x"
                  className="col-span-2"
                  rules={[{ validator: validateURL }]}
                >
                  <Input placeholder="https://twitter.com" />
                </Form.Item>
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 mt-3 md:mt-5 gap-2">
              <h3>Instagram profile (Optional)</h3>
              <span className="col-span-4 w-full">
                <Form.Item
                  name="instagram"
                  className="col-span-2"
                  rules={[{ validator: validateURL }]}
                >
                  <Input placeholder="https://instagram.com" />
                </Form.Item>
              </span>
            </div>

            <br />
            <hr />

            <div className="mt-6">
              <ProfileNavigationButtons
                onSave={handleSaveAndNext}
                isLoading={isUpdateTPLoading}
                hasForm={true}
                nextButtonLabel="Next"
                onNext={handleNext}
              />
            </div>

            <br />
            <hr />
          </div>
        </div>
      </Form>
    </>
  );
};
